import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Callout } from './primitives/Callout';
import { Link, useLocation } from 'react-router-dom';

interface Props {
  /** What the boundary protects, for the message ("this section"). */
  label?: string;
  /** "page" wraps a whole route: the header and navigation stay, and the
   * way out is the dashboard rather than "the rest of the project". */
  scope?: 'section' | 'page';
  /** When this changes (the path, usually) a shown error is cleared so the
   * next view starts clean. The children are never remounted for it: keying
   * a boundary by path would unmount the whole layout under it on every
   * navigation, and everything in it would reload. */
  resetKey?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * SectionErrorBoundary keeps one broken view from blanking the whole app.
 * Without it, a single unexpected value in one card (CO-73: a deployment
 * group without its component list) unmounted everything, header and
 * navigation included, and left an empty page with the reason only in the
 * console. The error is shown where the view would be, with a retry;
 * the rest of the project stays usable. Give it the location as `resetKey`
 * so moving to another section starts clean.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Section failed to render', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const page = this.props.scope === 'page';
    return (
      <Callout
        tone="danger"
        role="alert"
        testId={page ? 'page-error' : 'section-error'}
        title={`${this.props.label ?? (page ? 'This page' : 'This section')} could not be shown.`}
        actions={
          <>
            <button type="button" className="button button--secondary button--small" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
            {page ? (
              // A client-side link: the shell and its state stay, no full reload (CO-184, DS8).
              <Link className="button button--ghost button--small" to="/app">
                Go to the dashboard
              </Link>
            ) : (
              <button type="button" className="button button--ghost button--small" onClick={() => window.location.reload()}>
                Reload the page
              </button>
            )}
          </>
        }
      >
        <p>
          Commerce Ops hit an unexpected problem while displaying it.{' '}
          {page ? 'Everything else in Commerce Ops still works.' : 'The rest of the project still works.'} Try again, or reload the
          page; if it keeps happening, report it with the technical details.
        </p>
        <details className="quiet-disclosure callout__details">
          <summary>Technical details</summary>
          <code>{error.message}</code>
        </details>
      </Callout>
    );
  }
}

/** RouteErrorBoundary wraps the routed pages under the header (CO-87): a page
 * that throws while rendering shows why in place of the page, never a blank
 * app. A new path clears
 * a shown error without remounting the page's layout. */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <SectionErrorBoundary resetKey={location.pathname} scope="page">
      {children}
    </SectionErrorBoundary>
  );
}
